import { EReportTypeDownload } from 'redux/reducers/fiscalReports/constants';

export const endpoint = {
	auth: {
		LOGIN: '/api/auth/token',
		ACCOUNT_LOGIN: '/api/account',
		LOGOUT: '/api/user/logout',
		TOKEN_CODE: '/api/user/token/code',
		CONNECT: '/api/auth/connect',
		REFRESH_TOKEN: '/api/user/refresh/token',
	},
	monthlyFee: {
		GET_EMERCHANTS: '/api/emerchant',
		GET_MONTHLY_FEES: '/api/fee/monthly_fees',
		GET_FEE_CLASSES: '/api/fee/fee_classes',
		GET_MONTHLY_FEE_LIST: `/api/monthly_fee`,
		ADD_MONTHLY_FEE: `/api/monthly_fee`,
		DELETE_MONTHLY_FEE: (monthlyFeeId: number) => `/api/monthly_fee/${monthlyFeeId}`,
	},

	easFee: {
		ANNUAL_FEE_TYPE_OPTIONS: '/api/fee/annual_fee_types',
		ANNUAL_FEE_TYPE_LIST: '/api/fee/annual_fee_types',
		ANNUAL_FEE_TYPE_DETAILS: (id: number) => `/api/fee/annual_fee_types/${id}`,
		ANNUAL_FEE_TYPE_DELETE: (id: number) => `/api/fee/annual_fee_types/${id}`,
		ANNUAL_FEE_TYPE_CREATE: '/api/fee/annual_fee_types/create',
		ANNUAL_FEE_TYPE_UPDATE: (id: number) => `/api/fee/annual_fee_types/${id}/update`,

		MONTHLY_FEE_OPTIONS: '/api/fee/monthly_fees',
		MONTHLY_FEE_LIST: '/api/fee/monthly_fees',
		MONTHLY_FEE_DETAILS: (id: number) => `/api/fee/monthly_fees/${id}`,
		MONTHLY_FEE_DELETE: (id: number) => `/api/fee/monthly_fees/${id}`,
		MONTHLY_FEE_CREATE: '/api/fee/monthly_fees/create',
		MONTHLY_FEE_UPDATE: (id: number) => `/api/fee/monthly_fees/${id}/update`,

		INVOICING_FEE_TYPE_OPTIONS: '/api/fee/invoicing_fee_types',
		INVOICING_FEE_TYPE_LIST: '/api/fee/invoicing_fee_types',
		INVOICING_FEE_TYPE_DETAILS: (id: number) => `/api/fee/invoicing_fee_types/${id}`,
		INVOICING_FEE_TYPE_CREATE: '/api/fee/invoicing_fee_types/create',
		INVOICING_FEE_TYPE_UPDATE: (id: number) => `/api/fee/invoicing_fee_types/${id}/update`,

		FEE_CLASS_OPTIONS: '/api/fee/fee_classes',
		FEE_CLASS_LIST: '/api/fee/fee_classes',
		FEE_CLASS_DETAILS: (id: number) => `/api/fee/fee_classes/${id}`,
		FEE_CLASS_DELETE: (id: number) => `/api/fee/fee_classes/${id}`,
		FEE_CLASS_CREATE: '/api/fee/fee_classes/create',
		FEE_CLASS_UPDATE: (id: number) => `/api/fee/fee_classes/${id}/update`,
	},

	dashboard: {
		EM_TOTAL_TAXES: '/api/cache/fetch_em_total_taxes',
		EM_SALES_INFO: '/api/cache/fetch_em_sales_info',
		EM_MONTHLY_SALES_INFO: '/api/cache/fetch_em_monthly_sales_info',
	},
	payMaintenance: {
		ENCRYPT_INVOICE_NUMBER: '/api/maintenance/encryptInvoiceNumber',
	},
	/** Payment gateway admin API (`httpPay`, role `paymentgateway_admin`). */
	paymentGateway: {
		// Operator and system
		ME: '/api/admin/me',
		SYSTEM: '/api/admin/system',
		// Invoice Management (any operator, not only paymentgateway_admin): receipts of invoices paid online.
		// ?invoiceIds=1&invoiceIds=2 — the gateway binds repeated keys, not invoiceIds[]
		INVOICE_RECEIPTS: (invoiceIds: number[]) =>
			`/api/operator/invoices/receipts?${invoiceIds.map((id) => `invoiceIds=${encodeURIComponent(id)}`).join('&')}`,

		// Services and service_type mappings
		SERVICES: '/api/admin/services',
		SERVICE: (id: number) => `/api/admin/services/${id}`,
		SERVICE_TYPE_MAPPINGS: '/api/admin/service-type-mappings',
		SERVICE_TYPE_MAPPING: (serviceType: string) =>
			`/api/admin/service-type-mappings/${encodeURIComponent(serviceType)}`,

		// Debt transfer (payability) rules
		PAYABILITY_RULES: '/api/admin/payability-rules',
		PAYABILITY_RULE: (id: number) => `/api/admin/payability-rules/${id}`,
		PAYABILITY_RULES_EVALUATE: '/api/admin/payability-rules/evaluate',

		// Payment links and invoices
		PAYMENT_LINKS: '/api/admin/payment-links',
		INVOICE: (invoiceId: number) => `/api/admin/invoices/${invoiceId}`,

		// Legal entities and Airwallex accounts
		LEGAL_ENTITIES: '/api/admin/legal-entities',
		LEGAL_ENTITY: (id: number) => `/api/admin/legal-entities/${id}`,
		AIRWALLEX_ACCOUNTS: '/api/admin/airwallex-accounts',
		AIRWALLEX_ACCOUNT: (id: number) => `/api/admin/airwallex-accounts/${id}`,
		AIRWALLEX_ACCOUNT_WEBHOOKS_SYNC: (id: number) =>
			`/api/admin/airwallex-accounts/${id}/webhooks/sync`,
		AIRWALLEX_ACCOUNT_APPLE_PAY_DOMAINS_SYNC: (id: number) =>
			`/api/admin/airwallex-accounts/${id}/applepay-domains/sync`,
		AIRWALLEX_ACCOUNT_CREDENTIALS_CHECK: (id: number) =>
			`/api/admin/airwallex-accounts/${id}/credentials/check`,

		// Fees and DD FX
		FEES: '/api/admin/fees',
		FEES_PREVIEW: '/api/admin/fees/preview',
		FX_SETTINGS: '/api/admin/fx-settings',
		FX_SETTINGS_UPDATE: (legalEntityId: number) => `/api/admin/fx-settings/${legalEntityId}`,
		FX_SETTINGS_PREVIEW: '/api/admin/fx-settings/preview',

		// Schedule and runs
		SCHEDULE: '/api/admin/schedule',
		RECURRING_RUNS: '/api/admin/recurring/runs',
		RECURRING_RUNS_LIST: '/api/admin/recurring/runs/list',
		RECURRING_RUN: (id: string) => `/api/admin/recurring/runs/${id}`,
		RECURRING_CYCLES_LIST: '/api/admin/recurring/cycles/list',

		// Customer access, EAS updates pause and customer e-mails
		CLIENT_ACCESS: '/api/admin/client-access',

		// Customers and payment methods
		CUSTOMERS_LIST: '/api/admin/customers/list',
		CUSTOMER: (id: number) => `/api/admin/customers/${id}`,
		CUSTOMER_BY_CLIENT_NUMBER: (clientNumber: string) =>
			`/api/admin/customers/by-client-number/${encodeURIComponent(clientNumber)}`,
		CUSTOMER_UK_OVERRIDE: (id: number) => `/api/admin/customers/${id}/uk-override`,
		CUSTOMER_REFRESH: (id: number) => `/api/admin/customers/${id}/refresh`,
		CUSTOMER_SYNC_CONSENTS: (id: number) => `/api/admin/customers/${id}/sync-consents`,
		CUSTOMER_LEGACY_TRANSACTIONS_LIST: (id: number) =>
			`/api/admin/customers/${id}/legacy-transactions/list`,
		PAYMENT_METHOD_DISABLE: (id: number) => `/api/admin/payment-methods/${id}/disable`,
		PAYMENT_METHOD_SERVICES: (id: number) => `/api/admin/payment-methods/${id}/services`,

		// Payments
		PAYMENTS_LIST: '/api/admin/payments/list',
		PAYMENT: (transactionId: string) => `/api/admin/payments/${transactionId}`,
		PAYMENT_SYNC: (transactionId: string) => `/api/admin/payments/${transactionId}/sync`,
		PAYMENT_EAS_SYNC: (transactionId: string) => `/api/admin/payments/${transactionId}/eas-sync`,
		PAYMENTS_CHARGE_INVOICE: '/api/admin/payments/charge-invoice',
		PAYMENTS_CHARGE_CUSTOMER: '/api/admin/payments/charge-customer',

		// Refunds
		PAYMENT_REFUNDS: (transactionId: string) => `/api/admin/payments/${transactionId}/refunds`,
		REFUNDS_LIST: '/api/admin/refunds/list',
		REFUND: (id: number) => `/api/admin/refunds/${id}`,
		REFUND_SYNC: (id: number) => `/api/admin/refunds/${id}/sync`,

		// FX conversions
		FX_CONVERSIONS_LIST: '/api/admin/fx-conversions/list',
		FX_CONVERSION: (id: number) => `/api/admin/fx-conversions/${id}`,
		FX_CONVERSION_SYNC: (id: number) => `/api/admin/fx-conversions/${id}/sync`,
		FX_CONVERSION_RETRY: (id: number) => `/api/admin/fx-conversions/${id}/retry`,
		FX_CONVERSION_APPROVE: (id: number) => `/api/admin/fx-conversions/${id}/approve`,
		PAYMENT_CONVERT: (transactionId: string) => `/api/admin/payments/${transactionId}/convert`,

		// Notifications and receipts
		NOTIFICATIONS_LIST: '/api/admin/notifications/list',
		NOTIFICATION_RESEND: (id: number) => `/api/admin/notifications/${id}/resend`,
		NOTIFICATIONS_EMAIL_TYPES: '/api/admin/notifications/email-types',
		NOTIFICATIONS_RELINK_CAMPAIGN: '/api/admin/notifications/relink-campaign',
		PAYMENT_RECEIPT: (transactionId: string) => `/api/admin/payments/${transactionId}/receipt`,
		PAYMENT_RECEIPT_SEND: (transactionId: string) =>
			`/api/admin/payments/${transactionId}/receipt/send`,

		// Alerts, outbox, audit
		ALERTS_LIST: '/api/admin/alerts/list',
		ALERT: (id: number) => `/api/admin/alerts/${id}`,
		ALERT_ACKNOWLEDGE: (id: number) => `/api/admin/alerts/${id}/acknowledge`,
		ALERT_RESOLVE: (id: number) => `/api/admin/alerts/${id}/resolve`,
		ALERT_ROLLBACK_EAS: (id: number) => `/api/admin/alerts/${id}/actions/rollback-eas`,
		ALERT_REAPPLY_EAS: (id: number) => `/api/admin/alerts/${id}/actions/reapply-eas`,
		OUTBOX_LIST: '/api/admin/outbox/list',
		OUTBOX_MESSAGE: (id: number) => `/api/admin/outbox/${id}`,
		OUTBOX_RETRY: (id: number) => `/api/admin/outbox/${id}/retry`,
		AUDIT_LIST: '/api/admin/audit/list',

		// Webhooks and reconciliation
		WEBHOOK_EVENTS_LIST: '/api/admin/webhooks/events/list',
		WEBHOOK_EVENT: (id: number) => `/api/admin/webhooks/events/${id}`,
		WEBHOOK_EVENT_REPLAY: (id: number) => `/api/admin/webhooks/events/${id}/replay`,
		RECONCILIATION_RUN: '/api/admin/reconciliation/run',
		RECONCILIATION_LAST: '/api/admin/reconciliation/last',
	},
	user: {
		ORGANIZATIONS: '/organizations',
		USER_INFO: '/api/user',
		GET_NEW_MESSAGE_COUNT: '/api/get_new_message_count',
		FETCH_MESSAGES: '/api/fetch_messages',
		ALL_USERS: '/api/user/all',
		SAVE_SESSION_DATA: '/api/user/token/save_session_data',
	},
	userRoles: {
		GET_ROLES: '/api/user_roles/roles',
		GET_GROUPED: '/api/user_roles/grouped',
		GET_USER_ROLES: '/api/user_roles',
		ASSIGN_ROLES: '/api/user_roles/assign',
		UPDATE_USER_ROLE: '/api/user_roles/update',
		REMOVE_USER_ROLE: (id: number) => `/api/user_roles/remove/${id}`,
		GET_USER_ROLES_BY_USER_ID: (userId: string) => `/api/user_roles/${userId}`,
	},
	vatReport: {
		NEW_VAT_REPORT: 'api/vatreport/ioss-new',
		VAT_REPORT_STATUS: (taskId: string) => `api/vatreport/${taskId}`,
		VAT_REPORT_FILE: (taskId: string) => `api/vatreport/${taskId}/file`,
		NEW_VAT_REPORT_WITH_PAYMENT: 'api/vatreport/ioss-new-with-issues',
		NON_UNION_VAT_REPORT: 'api/vatreport/non-union-new',
		NON_UNION_VAT_REPORT_WITH_PAYMENT: 'api/vatreport/non-union-new-with-issues',
		NON_UNION_VAT_XML_REPORT: 'api/vatreport/non-union-xml-new',
		NON_UNION_VAT_XML_REPORT_WITH_PAYMENT: 'api/vatreport/non-union-xml-new-with-issues',
		UNION_VAT_REPORT: 'api/vatreport/union',
		UNION_VAT_REPORT_WITH_PAYMENT: 'api/vatreport/union-with-issues',
		GET_VAT_REPORTS: '/api/vatreport',
		SWISS_POST_VAT_REPORT: 'api/vatreport/swiss',
		EU_DDP_VAT_REPORT: 'api/vatreport/eu_ddp',

		// VAT_REPORT_STATUS_WITH_PAYMENT: (taskId: string) => `/vatreport/${taskId}`,
		// VAT_REPORT_FILE_WITH_PAYMENT: (taskId: string) => `/vatreport/${taskId}/file`,
	},
	errorCorrections: {
		ORDER_CORRECTIONS: '/api/order_corrections',
		ORDER_CORRECTION: (errorId: number) => `/api/order_corrections/${errorId}`,
		ERROR_TYPES: '/api/order_corrections/error_types',
		SCRIPTS_HISTORY: (errorId: number) => `/api/order_corrections/${errorId}/scripts_history`,
		UPDATE_COMMENT: (errorId: number) => `/api/order_corrections/${errorId}/update_comment`,
		UPDATE_COMMUNICATION_WITH_CLIENT: (errorId: number) =>
			`/api/order_corrections/${errorId}/update_communication_with_client`,
		UPDATE_PROPOSED_SOLUTION: (errorId: number) =>
			`/api/order_corrections/${errorId}/update_proposed_solution`,
		SET_RESPONSIBLE_USER: (errorId: number) =>
			`/api/order_corrections/${errorId}/set_responsible_user`,
		SET_STATUS: (errorId: number) => `/api/order_corrections/${errorId}/set_status`,
		START_SCRIPT: (errorId: number, scriptId: number) =>
			`/api/order_corrections/${errorId}/start_script/${scriptId}`,
		SCRIPTS: '/api/order_corrections/scripts',
		USER_STATISTICS: '/api/order_corrections/user_statistics',
		LAUNCHED_SCRIPTS: (orderId: number) =>
			`/api/order_corrections/by_order_id/${orderId}/launched_scripts`,
		START_SCRIPT_BY_ORDER: (id: number, scriptId: number) =>
			`/api/order_corrections/by_order_id/${id}/start_script/${scriptId}`,
	},
	emProductFileUploads: {
		UPLOADED_FILES: '/api/em_product_files/uploaded_files',
		UPLOAD: '/api/em_product_files/upload',
		DOWNLOAD: (id: number) => `/api/em_product_files/download/${id}`,
		DOWNLOAD_PROCESSED: (id: number) => `/api/em_product_files/download_processed_file/${id}`,
	},
	swissPost: {
		GET_SWISS_POST_AVAILABLE_EMS: '/api/swiss_post/available_ems',
		GET_SWISS_POST_CONNECTED_EMS: '/api/swiss_post/connected_ems',
		CREATE_OR_UPDATE_SWISS_POST: '/api/swiss_post/create_or_update',
	},
	swedBank: {
		GET_SWED_BANK_DOCKS: '/api/swedbank',
		UPLOAD_STATEMENT: '/api/swedbank/upload_statement',
		SEARCH_INVOICE: '/api/swedbank/search_invoice',
		MARK_AS_PROCESSED: '/api/swedbank/mark',
		ATTACH_VAT_REIMBURSEMENT: `/api/swedbank/attach_vat_reimbursement`,
	},
	mtd: {
		GET_MTD_REPORTS: '/api/hmrc/reports',
		GET_MTD_REPORT: (reportId: number) => `/api/hmrc/reports/${reportId}`,
		ADD_MTD_COMMENT: (reportId: string) => `/api/hmrc/reports/${reportId}/comment`,
		SEND_MTD_FILE_REPORT: (reportId: string) => `/api/hmrc/reports/${reportId}/send_to_hmrc`,
		CALCULATE_MTD_REPORT_DATA: (reportId: string) => `/api/hmrc/reports/${reportId}/calculate_data`,
		DOWNLOAD_MTD_REPORT_PDF: (reportId: string) => `/api/hmrc/reports/${reportId}/download_pdf`,
		UPDATE_OBLIGATIONS: (emId: number, startDate: string) =>
			`/api/hmrc/reports/update_obligations?emId=${emId}&startDate=${startDate}`,
		GET_MANUAL_PROCESSING: (emId: number) => `/api/hmrc/manual_processing/${emId}`,
		UPDATE_MANUAL_PROCESSING: '/api/hmrc/manual_processing',
	},
	eudr: {
		GET_PLANS: '/api/eudr/plans',
		GET_SERVICE_STATUSES: '/api/eudr/service_statuses',
		REG_PARSE: '/api/eudr/reg_parse',
		REGISTER: '/api/eudr/register',
		EUDR_ITEMS: '/api/eudr',
		GENERATE_INVOICE: (id: number) => `/api/eudr/${id}/invoice`,
		DOWNLOAD_LAST_INVOICE: (id: number) => `/api/eudr/${id}/invoice_pdf`,
		ACTIVATE_EUDR: (id: number) => `/api/eudr/${id}/activation`,
	},
	euDdp: {
		SEARCH_RECORDS: '/api/eu_ddp',
		SEARCH_DELIVERY_OPERATORS: '/api/eu_ddp/delivery_operators/search',
		CREATE_DELIVERY_OPERATOR: '/api/eu_ddp/delivery_operators',
		UPDATE_DELIVERY_OPERATOR: (id: number) => `/api/eu_ddp/delivery_operators/${id}`,
		SEARCH_TARIFFS: '/api/eu_ddp/tariffs/search',
		CREATE_TARIFF: '/api/eu_ddp/tariffs',
		UPDATE_TARIFF: (id: number) => `/api/eu_ddp/tariffs/${id}`,
		SEARCH_PRICING_PLANS: '/api/eu_ddp/pricing_plans/search',
		CREATE_PRICING_PLAN: '/api/eu_ddp/pricing_plans',
		UPDATE_PRICING_PLAN: (id: number) => `/api/eu_ddp/pricing_plans/${id}`,
		SEARCH_ORDERS: '/api/eu_ddp/orders/search',
		GET_ORDER: (orderId: number) => `/api/eu_ddp/orders/${orderId}`,
		PRINT_LABELS: '/api/eu_ddp/orders/label_list',
		GET_OMNIVA_LABEL: (orderId: number) => `/api/eu_ddp/orders/${orderId}/omniva_label`,
		GET_INVOICE_PROFORMA: (orderId: number) => `/api/eu_ddp/orders/${orderId}/invoice_proforma`,
	},
	gpsr: {
		REG_PARSE: '/api/gpsr/reg_parse',
		REGISTER: '/api/gpsr/register',
		GET_PLANS: '/api/gpsr/plans',
		SERVICE_STATUSES: '/api/gpsr/service-statuses',
		GPSR_ITEMS: '/api/gpsr',
		UPDATE_GPSR_SERVICE_SETTINGS: (id: number) => `/api/gpsr/${id}/update_gpsr_service_settings`,
		REMOVE_ON_RENEWAL: (id: number) => `/api/gpsr/${id}/remove_on_renewal`,
		GET_PRODUCT_TYPES: (emId: number) => `/api/gpsr/${emId}/product_type`,
		ADD_PRODUCT_TYPE: (emId: number) => `/api/gpsr/${emId}/product_type`,
		DELETE_PRODUCT_TYPE: (emId: number, productTypeId: number) =>
			`/api/gpsr/${emId}/product_type/${productTypeId}`,
		GET_REGULATIONS: (emId: number, productTypeId: number) =>
			`/api/gpsr/${emId}/product_type/${productTypeId}/regulations`,
		ADD_REGULATION: (emId: number, productTypeId: number) =>
			`/api/gpsr/${emId}/product_type/${productTypeId}/regulations`,
		DELETE_REGULATION: (emId: number, productTypeId: number, regulationId: number) =>
			`/api/gpsr/${emId}/product_type/${productTypeId}/regulations/${regulationId}`,
		GENERATE_INVOICE: (invoiceId: number) => `/api/gpsr/${invoiceId}/invoice`,
		GET_PRODUCT_TYPE_REFERENCES: `/api/product_types`,
		GET_PRODUCT_TYPE_REGULATIONS_GROUP_TYPES: `/api/product_type_regulations/group_types`,
		GET_PRODUCT_TYPE_REGULATIONS_REFERENCES: `/api/product_type_regulations`,
		GET_REGULATIONS_REFERENCES: `/api/regulations`,
		GET_TECH_FILE: (emId: number, productTypeId: number) =>
			`/api/gpsr/${emId}/product_type/${productTypeId}/tech_file`,
		GET_D_O_C_FILE: (emId: number, productTypeId: number) =>
			`/api/gpsr/${emId}/product_type/${productTypeId}/doc`,
		CANCEL_GPSR: (id: number) => `/api/gpsr/${id}/cancellation`,
		ACTIVATE_GPSR: (id: number) => `/api/gpsr/${id}/activation`,
		DOWNLOAD_LAST_INVOICE: (id: number) => `/api/gpsr/${id}/invoice_pdf`,
		GET_INVOICES_LIST: `/api/gpsr/merchant_invoice`,
		DOWNLOAD_INVOICE: (emId: number, invoiceId: number) =>
			`/api/gpsr/${emId}/download_invoice/${invoiceId}`,
		CHANGE_STATUS: (id: number, statusId: number) => `/api/gpsr/${id}/change_status/${statusId}`,
		REFERRAL: '/api/gpsr/referral',
	},
	vatReimbursement: {
		VAT_REIMBURSEMENTS: '/api/vat_reimbursements',
		MONEY_RECEIVED: '/api/vat_reimbursements/money_received',
		RECEIVE_VAT_REIMBURSEMENT: (id: number) =>
			`/api/vat_reimbursements/${id}/receive_vat_reimbursement`,
		UPDATE_COMMENT: (id: number) => `/api/vat_reimbursements/${id}/update_comment`,
	},
	salesProcessing: {
		UPLOADED_FILES: '/api/em_sales_reports/uploaded_files',
		PROCESS_FILE: `/api/em_sales_reports/process_file`,
		DOWNLOAD_FILE: (fileId: number) => `/api/em_sales_reports/download_file/${fileId}`,
		PROCESSING_TYPES: '/api/em_sales_reports/processing_types',
		FILE_STATUSES: `/api/file_statuses`,
		INVOICING_FEE_TYPES: '/api/em_sales_reports/em_invoicing_fee_types',
	},
	emSalesReports: {
		UPLOADED_FILES: '/api/em_sales_reports/uploaded_files',
		PROCESSING_TYPES: '/api/em_sales_reports/processing_types',
		PROCESS_FILE: '/api/em_sales_reports/process_file',
		DOWNLOAD_FILE: (fileId: number) => `/api/em_sales_reports/download_file/${fileId}`,
		INVOICING_FEE_TYPES: '/api/em_sales_reports/em_invoicing_fee_types',
	},
	massPostSaleJobs: {
		JOBS: '/api/mass_post_sale_jobs',
		ORDERS: '/api/mass_post_sale_jobs/orders',
	},
	superset: {
		DASHBOARDS: '/api/superset/dashboards',
		TOKEN: '/api/superset/token',
	},
	referrals: {
		GET_REFERRALS_PARTNERS: '/api/referrals/referral_partners',
		CREATE_REFERRAL_PARTNERS: '/api/referrals/referral_partners/create',
		GET_PARTNERS: '/api/referrals/partners',
		CREATE_PARTNER: '/api/referrals/partners/create',
		UPDATE_PARTNER: '/api/referrals/partners/update',
		GET_AVAILABLE_EMS: '/api/referrals/referral_partners/available_merchants',
		GET_PARTNER_REPORTS: '/api/referrals/partner_reports',
		DOWNLOAD_PARTNER_REPORT: (id: number) => `/api/referrals/partner_reports/${id}/download`,
	},
	emManagement: {
		GET_EM_MANAGEMENT_LIST: '/api/emerchant/list',
		GET_EM_INVOICE_LIST: '/api/visualize/load_invoice',
		GET_EM_BY_ID: (id: number) => `/api/emerchant/${id}/get_em_by_id`,
		GET_EM_BY_ID_FULL: (id: number) => `/api/emerchant/${id}/get_by_id`,
		UPDATE_EM: (id: number) => `/api/emerchant/${id}/update_em`,
		UPDATE_EMERCHANT: (id: number) => `/api/emerchant/${id}/update`,
		// dashboard users subscriptions
		GET_DASHBOARD_USERS_SUBSCRIPTIONS: (emId: number) =>
			`/api/emerchant/${emId}/dashboard_users_subscriptions`,
		UPDATE_DASHBOARD_USERS_SUBSCRIPTIONS: (emId: number) =>
			`/api/emerchant/${emId}/dashboard_users_subscriptions`,
		UNBLOCK_EMERCHANT: (id: number) => `/api/emerchant/${id}/unblock`,
		BLOCK_EMERCHANT: (id: number) => `/api/emerchant/${id}/block`,
		ACTIVATE_EMERCHANT: (emId: number) => `/api/emerchant/${emId}/activate`,
		DUPLICATE_EMERCHANT: (emId: number) => `/api/emerchant/${emId}/duplicate`,
		ACTIVATE_EU_DDP: (emId: number) => `/api/eu_ddp/${emId}/activate`,
		// em sites
		GET_EM_SITES_LIST: (emId: number) => `/api/em_sites/list/${emId}`,
		CREATE_EM_SITE: `/api/em_sites/create`,
		UPDATE_EM_SITE: (id: number) => `/api/em_sites/update/${id}`,
		DELETE_EM_SITE: (id: number) => `/api/em_sites/delete/${id}`,
		EM_ENUMS_BY_TYPE: '/api/emerchant/enums/by_type',
		DISTINCT_ENUM_NAMES: '/api/emerchant/distinct_enum_names',
		GET_SPECIAL_REGISTRATIONS: (emId: number) => `/api/emerchant/${emId}/special_registrations`,
		ADD_SPECIAL_REGISTRATION: (emId: number) => `/api/emerchant/${emId}/add_special_registration`,
		UPDATE_SPECIAL_REGISTRATION: (emId: number) =>
			`/api/emerchant/${emId}/update_special_registration`,
		ADD_SCHEDULE_TERMINATION: (emId: number) => `/api/emerchant/${emId}/add_schedule_termination`,
		DELETE_SCHEDULE_TERMINATION: (emId: number) =>
			`/api/emerchant/${emId}/delete_schedule_termination`,
		GET_ADDITIONAL_SERVICES: '/api/additional_services',
		CREATE_ADDITIONAL_SERVICE: '/api/additional_services/create',
		UPDATE_ADDITIONAL_SERVICE: (id: number) => `/api/additional_services/${id}/update`,
		GET_EM_ADDITIONAL_SERVICES: (emId: number) => `/api/em_additional_services/${emId}`,
		CREATE_EM_ADDITIONAL_SERVICE: '/api/em_additional_services/create',
		UPDATE_EM_ADDITIONAL_SERVICE: (emId: number) => `/api/em_additional_services/${emId}/update`,
		EM_ADDITIONAL_SERVICE_INVOICE: (id: number) => `/api/em_additional_services/${id}/invoice`,
		EM_ADDITIONAL_SERVICE_CANCEL: (id: number) => `/api/em_additional_services/${id}/cancel`,
		EM_INVOICE_PDF: (emId: number, invoiceId: number) => `/api/invoice/${emId}/${invoiceId}/pdf`,
		// em comments
		GET_EM_COMMENTS: '/api/em_comments',
		// em notifications
		GET_EM_NOTIFICATIONS: '/api/em_comments',
		GET_EM_COMMENT: (id: number) => `/api/em_comments/${id}`,
		CREATE_EM_COMMENT: '/api/em_comments/create',
		UPDATE_EM_COMMENT: (id: number) => `/api/em_comments/${id}/update`,
		REGISTER_NOMINATED_PERSON_ACCOUNT: `/api/register/nominated_person_account`,
		GET_EM_PAYMENT_METHODS: (emId: number) => `/api/emerchant/payment_methods/${emId}`,
	},
	emAutomationScripts: {
		GET_AVAILABLE_SCRIPTS: '/api/em_automation_scripts/available_scripts',
		GET_ASSIGNED_SCRIPTS: (emId: number) => `/api/em_automation_scripts/${emId}/assigned_scripts`,
		ASSIGN_SCRIPT: '/api/em_automation_scripts/assign_script',
		REMOVE_SCRIPT: (id: number, emId: number) =>
			`/api/em_automation_scripts/${id}/remove_script?emId=${emId}`,
	},
	country: {
		GET_COUNTRY_LIST: '/api/country/all',
		COUNTRY_LIST: '/api/country',
		COUNTRY_DETAILS: (id: number) => `/api/country/${id}`,
		COUNTRY_UPDATE: (id: number) => `/api/country/${id}/update`,
	},
	currency: {
		CURRENCY_LIST: '/api/currency',
		CURRENCY_DETAILS: (id: number) => `/api/currency/${id}`,
		CURRENCY_UPDATE: (id: number) => `/api/currency/${id}/update`,
	},
	exchangeRates: {
		DAILY_LIST: '/api/daily_exchange_rates',
		DAILY_REFRESH: '/api/daily_exchange_rates/update',
		ANNUAL_LIST: '/api/annual_exchange_rates',
		GBP_MONTHLY_LIST: '/api/exchange_rates_per_month_gbp',
		EU_MONTHLY_LIST: '/api/exchange_rates_per_month',
		NON_EU_LIST: '/api/exchange_rates_non_eu',
	},
	taxDuties: {
		COUNTRY_VAT_RATE_LIST: '/api/country-vat-rate',
		COUNTRY_VAT_RATE_SYNC: '/api/country-vat-rate/sync',
		COUNTRY_VAT_RATE_UPLOAD: '/api/country-vat-rate/upload_rates',
		COUNTRY_VAT_RATE_NON_EU_LIST: '/api/country-vat-rate/non-eu',
		SUPPORTED_NON_EU_COUNTRY_LIST: '/api/country-vat-rate/non-eu-country-list',
		HS6P_CODE_LIST: '/api/country-vat-rate/hs6p-codes',
		CUSTOMS_DUTY_RATE_LIST: '/api/country-customs-duty-rate',
		SPECIAL_TERRITORY_LIST: '/api/special-territory',
		SPECIAL_TERRITORY_CREATE: '/api/special-territory/create',
		SPECIAL_TERRITORY_UPDATE: (id: number) => `/api/special-territory/${id}/update`,
		SPECIAL_TERRITORY_POSTAL_CODE_LIST: '/api/special-territory/postal-codes',
		SPECIAL_TERRITORY_POSTAL_CODE_CREATE: '/api/special-territory/postal-codes/create',
		SPECIAL_TERRITORY_POSTAL_CODE_UPDATE: (id: number) =>
			`/api/special-territory/postal-codes/${id}/update`,
	},
	fiscalReports: {
		GET_FISCAL_VAT_LIST_INFO_FILTER: '/api/config/fiscal_vat_list_info_filter',
		GET_FISCAL_REPORT_LIST: '/api/visualize/load_fiscal_report_list',
		UPDATE_FISCAL_REPORT_PAYMENT: '/api/em_vat_fiscal_report/update_fiscal_report_payment',
		UPDATE_FISCAL_REPORT_COMMENT: '/api/persistence/update',
		SEND_PAID: '/api/mtareports/send_paid',
		SEND_REPORT: (reportId: string) => `/api/mtareports/${reportId}/send`,
		CANCEL_REPORT: (reportId: string) => `/api/mtareports/${reportId}/cancel`,
		DOWNLOAD_FISCAL_PDF: (emId: number, month: number, year: number) =>
			`/api/em_vat_report_fiscal/import/${emId}/${month}/${year}/pdf/undefined`,
		DOWNLOAD_FISCAL_CSV: (
			emId: number,
			month: number,
			year: number,
			countryCode: string,
			scheme: string,
			type: string,
		) =>
			`/api/em_vat_report_fiscal/${scheme}/${emId}/${month}/${year}/${type}${
				scheme !== EReportTypeDownload.NON_EU_IOSS ? `/${countryCode}` : ''
			}`,
		DOWNLOAD_FISCAL_XLSX: (emId: number, month: number, year: number, scheme: string) =>
			`/api/em_vat_report_fiscal/${scheme}/${emId}/${month}/${year}/xlsx/undefined`,
		EXPORT_FISCAL_XLSX: '/api/v2/export/xlsx',
	},
	accessManagement: {
		GET_EM_KEYCLOAK_USERS: (emId: number) => `/api/access_management/${emId}/keycloak_users`,
		GET_KEYCLOAK_GROUPS: '/api/access_management/keycloak_groups',
		GET_KEYCLOAK_USERS: '/api/access_management/keycloak_users',
		ATTACH_KEYCLOAK_USER: '/api/access_management/keycloak_users/attach',
		DETACH_KEYCLOAK_USER: '/api/access_management/keycloak_users/detach',
		CREATE_KEYCLOAK_USER: '/api/access_management/keycloak_users/create',
		SET_KEYCLOAK_USER_ENABLED: '/api/access_management/keycloak_users/enabled',
		RESET_KEYCLOAK_USER_PASSWORD: '/api/access_management/keycloak_users/reset_password',
		CONNECTED_EMERCHANTS: '/api/access_management/connected-emerchants',
	},
	changeHistory: {
		GET_AUDIT_LOGS: '/api/audit_logs',
		GET_AUDIT_LOGS_BY_EM: (id: number) => `/api/audit_logs/${id}/em`,
		GET_AUDIT_LOGS_BY_USER: (userId: number) => `/api/audit_logs/${userId}/user`,
	},
	emVat: {
		LIST: '/api/em_vat/list',
		CREATE: (vatType: string) => `/api/em_vat/${vatType}/create`,
		UPDATE: (vatType: string, id: number) => `/api/em_vat/${vatType}/update/${id}`,
		DELETE: (vatType: string, id: number) => `/api/em_vat/${vatType}/delete/${id}`,
	},
	emVatReports: {
		LIST: '/api/em_vat_reports/list',
	},
	attachments: {
		GET_TYPES: '/api/attachment_types',
		LIST: (emId: number) => `/api/emerchant/${emId}/attachments`,
		UPLOAD: (emId: number) => `/api/emerchant/${emId}/attachments`,
		UPLOAD_LINKS: (emId: number) => `/api/emerchant/${emId}/attachments/upload-links`,
		GET_ONE: (emId: number, attachmentId: number) =>
			`/api/emerchant/${emId}/attachments/${attachmentId}`,
		DELETE: (emId: number, attachmentId: number) =>
			`/api/emerchant/${emId}/attachments/${attachmentId}`,
		DOWNLOAD: (attachmentId: number) => `/api/attachments/${attachmentId}`,
	},
	jotform: {
		SEARCH: '/api/jotform',
		GET_BY_ID: (jotformId: number) => `/api/jotform/${jotformId}`,
		CREATE_EMERCHANT: (jotformId: number) => `/api/jotform/${jotformId}/emerchant`,
	},
	generalAttachments: {
		LIST: '/api/general_attachments',
		CREATE: '/api/general_attachments',
		GET_ONE: (attachmentId: number) => `/api/general_attachments/${attachmentId}`,
		UPDATE: (attachmentId: number) => `/api/general_attachments/${attachmentId}`,
		DELETE: (attachmentId: number) => `/api/general_attachments/${attachmentId}`,
		ADD_VERSION: (attachmentId: number) => `/api/general_attachments/${attachmentId}/version`,
	},
	emails: {
		LIST: '/api/notification/emails/list',
		TYPES: '/api/notification/emails/types',
	},
	sms: {
		SEND: '/api/notification/sms/send',
		LIST: '/api/notification/sms/list',
		TYPES: '/api/notification/sms/types',
	},
	orderManagement: {
		GET_ORDER_LIST: '/api/visualize/load',
		GET_ORDER_DISCOUNTS: '/api/visualize/load',
		GET_ORDER_DISCOUNT_BREAKDOWN: '/api/visualize/load',
		GET_ORDER_SPLITS: (id: number) => `/api/visualize/order_splits/${id}`,
		GET_ORDER_ITEMS: (id: number) => `/api/visualize/orderitems/${id}`,
		GET_SPLIT_ITEMS: (splitId: string) => `/api/visualize/split_items/${splitId}`,
		GET_SWISS_DECLARATIONS: (orderId: number) => `/api/orders/${orderId}/swiss_declarations`,
		GET_SWISS_DECLARATION: (orderId: number, id: number) =>
			`/api/orders/${orderId}/swiss_declarations/${id}`,
	},
	emVatCorrectionsImport: {
		LOAD: '/api/visualize/load',
		ACTION: '/api/vat_corrections/action',
		FETCH_SUPPORTIVE_DOCUMENT: '/api/vat_corrections/attachments/download',
	},
	shopifyStores: {
		GET_STORES_BY_CLIENT_ID1: '/api/stores',
		GET_STORES_BY_CLIENT_ID: '/api/shopify_app/get_stores_by_client_id',
		GET_STORE_SETTINGS: '/api/shopify_app/get_store_settings',
		GET_STORE_SETTINGS_V2: '/api/stores/settings',
		UPDATE_STORE_SETTINGS: '/api/shopify_app/update_store_settings',
		UPDATE_STORE_SETTINGS_V2: '/api/stores/settings',
		GET_STORE_SALES_CHANNELS: '/api/shopify_app/get_store_sales_channels',
		GET_STORE_SALES_CHANNELS_V2: '/api/stores/sales-channels',
		UPDATE_STORE_SALES_CHANNELS: '/api/shopify_app/update_store_sales_channels',
		UPDATE_STORE_SALES_CHANNELS_V2: '/api/stores/sales-channels',
		SYNC_STORE: '/api/shopify_app/run_sync',
		SYNC_STORE_V2: '/api/stores/sync',
		LIST_SALES_CHANNELS_CATALOG: '/api/shopify_app/list_sales_channels_catalog',
		LIST_SALES_CHANNELS_CATALOG_V2: '/api/stores/sales-channels/catalog',
		CREATE_SALES_CHANNEL: '/api/shopify_app/create_sales_channel',
		CREATE_SALES_CHANNEL_V2: '/api/stores/sales-channels/catalog',
		GET_SALES_CHANNEL: (channelId: number) => `/api/stores/sales-channels/catalog/${channelId}`,
		GET_SALES_CHANNEL_V2: (channelId: number) => `/api/stores/sales-channels/catalog/${channelId}`,
		UPDATE_SALES_CHANNEL: '/api/shopify_app/update_sales_channel',
		UPDATE_SALES_CHANNEL_V2: (channelId: number) =>
			`/api/stores/sales-channels/catalog/${channelId}`,
		DELETE_SALES_CHANNEL: '/api/shopify_app/delete_sales_channel',
		DELETE_SALES_CHANNEL_V2: (channelId: number) =>
			`/api/stores/sales-channels/catalog/${channelId}`,
	},
	easInfo: {
		LIST: '/api/eas_info',
		GROUPS: '/api/eas_info/groups',
	},
	invoiceManagement: {
		GET_INVOICE_LIST: '/api/visualize/load_invoice',
		UPDATE_INVOICE_PAYMENT: '/api/invoice/update',
		NULLIFY_INVOICE: '/api/invoice_management/nullify',
		REMOVE_LPF: '/api/invoice_management/remove_lpf',
		DOWNLOAD_INVOICE_PDF: (emId: number, invoiceId: number) =>
			`/api/invoice/${emId}/${invoiceId}/pdf`,
		DOWNLOAD_INVOICE_UDR_CSV: (emId: number, invoiceId: number) =>
			`/api/invoice_management/invoice_udr/${emId}/${invoiceId}`,
		UPDATE_INVOICE_COMMENT: '/api/invoice/update_comment',
		SEND_EMAIL_FOR_INVOICE: (invoiceId: number) =>
			`/api/notification/sendEmailForInvoice/${invoiceId}`,
		UPDATE_INVOICE_GPSR: '/api/persistence/update',
		INVOICE_LIST_EXCEL_EXPORT: '/api/invoice/invoice_list_excel_export',
		DOWNLOAD_INVOICE_ARCHIVE: '/api/invoice_management/download_invoice_archive',
		SEARCH_CREDIT_NOTES: '/api/credit_notes/search',
	},
	reports: {
		REPORT_TYPES: '/api/reports/report_types',
		SEARCH_AVAILABLE_REPORTS: '/api/reports/available_reports/search',
		DOWNLOAD_AVAILABLE_REPORT: (id: number) => `/api/reports/available_reports/${id}/download`,
	},
	prp: {
		GET_SETTINGS: (emId: number) => `/api/prp/${emId}/settings`,
		UPDATE_SETTINGS: (emId: number) => `/api/prp/${emId}/settings`,
		ACTIVATE: (emId: number) => `/api/prp/${emId}/activate`,
		POSTAL_OPERATOR_SETTINGS_LIST: (emId: number) => `/api/prp/${emId}/postal_operator_settings`,
		UPDATE_POSTAL_OPERATOR_SETTINGS: (emId: number, id: number) =>
			`/api/prp/${emId}/postal_operator_settings/${id}/update`,
	},
	supportedPostalOperators: {
		LIST: '/api/supported_postal_operators',
		DETAILS: (id: number) => `/api/supported_postal_operators/${id}`,
		DELETE: (id: number) => `/api/supported_postal_operators/${id}`,
		CREATE: '/api/supported_postal_operators/create',
		UPDATE: (id: number) => `/api/supported_postal_operators/${id}/update`,
	},
	ppwr: {
		SEARCH_PACKAGING_TYPES: '/api/ppwr_packaging_types/search',
		PACKAGING_TYPE_DETAILS: (id: number) => `/api/ppwr_packaging_types/${id}`,
		CREATE_PACKAGING_TYPE: '/api/ppwr_packaging_types',
		UPDATE_PACKAGING_TYPE: (id: number) => `/api/ppwr_packaging_types/${id}`,
		UPDATE_PACKAGING_TYPE_HANDLER: (id: number) => `/api/ppwr_packaging_types/${id}/handler`,
		SEARCH_FEES: '/api/ppwr_fees/search',
		FEE_DETAILS: (id: number) => `/api/ppwr_fees/${id}`,
		CREATE_FEE: '/api/ppwr_fees',
		UPDATE_FEE: (id: number) => `/api/ppwr_fees/${id}`,
		SEARCH_EM_SETTINGS: '/api/ppwr/em_settings/search',
		EM_SETTINGS_DETAILS: (emId: number) => `/api/ppwr/em_settings/${emId}`,
		UPDATE_EM_SETTINGS: (emId: number) => `/api/ppwr/em_settings/${emId}`,
		SEARCH_JOTFORMS: '/api/jotform/ppwr',
		JOTFORM_DETAILS: (jotformId: number) => `/api/jotform/ppwr/${jotformId}`,
		ACTIVATE_SERVICE: (jotformId: number) => `/api/jotform/ppwr/${jotformId}/emerchant`,
		SEARCH_EM_PACKAGING_TYPES: (emId: number) =>
			`/api/emerchant/${emId}/ppwr/packaging-types/search`,
		UPDATE_EM_PACKAGING_TYPE: (emId: number, pckgTypeId: number) =>
			`/api/emerchant/${emId}/ppwr/packaging-types/${pckgTypeId}`,
		UPLOAD_EM_PACKAGING_TYPE_DOCUMENT: (emId: number, pckgTypeId: number) =>
			`/api/emerchant/${emId}/ppwr/packaging-types/${pckgTypeId}/documents`,
		DOWNLOAD_EM_PACKAGING_TYPE_DOCUMENT: (emId: number, documentId: number) =>
			`/api/emerchant/${emId}/ppwr/packaging-types/documents/${documentId}/download`,
		DELETE_EM_PACKAGING_TYPE_DOCUMENT: (emId: number, documentId: number) =>
			`/api/emerchant/${emId}/ppwr/packaging-types/documents/${documentId}`,
		SEARCH_EM_COUNTRY_EXCLUSIONS: '/api/ppwr/em_country_exclusions/search',
		CREATE_EM_COUNTRY_EXCLUSION: '/api/ppwr/em_country_exclusions',
		UPDATE_EM_COUNTRY_EXCLUSION: (id: number) => `/api/ppwr/em_country_exclusions/${id}`,
	},
};
